/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import user from 'js/lib/user';

import { InlineNotification, LoadingSection, Pagination, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { extractEventId } from 'bundles/product-card/components/utils/productCardV2Utils';
import { submitResponse } from 'bundles/reviews-common/utils/ReviewHelpfulAPIUtils';
import ReviewTableCard, { getReviewAuthorName } from 'bundles/reviews/components/ReviewTableCard';
import ReviewsTableToolbar from 'bundles/reviews/components/ReviewsTableToolbar';
import { ALL_STAR_VALUES, REVIEWS_TABLE_DEFAULT_PAGE_SIZE } from 'bundles/reviews/constants';
import {
  type CourseReviewElement,
  useCourseReviews,
} from 'bundles/unified-description-page-common/hooks/useCourseReviews';

import _t from 'i18n!nls/reviews';

const MAX_START_PLUS_LIMIT = 10000;

const DEFAULT_PAGE_SIZE = REVIEWS_TABLE_DEFAULT_PAGE_SIZE;
const PAGE_SIZE_VALUES = [10, 20, 30, 40, 50];

const styles = {
  root: css`
    padding: 0 0 var(--cds-spacing-200) 0;
    margin-bottom: var(--cds-spacing-400);
    scroll-margin-top: 132px;
    width: 100%;
  `,
  header: css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  cardList: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  loading: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  `,
  pagination: css`
    display: flex;
    margin-top: var(--cds-spacing-400);
  `,
};

type CourseFilterOption = {
  slug: string;
  name: string;
};

type ReviewsTableProps = {
  courseId: string;
  // Only set by the specialization/professional-certificate reviews page -- see
  // ReviewsTableToolbar for details.
  courseFilterOptions?: CourseFilterOption[];
  selectedCourseSlug?: string;
  onCourseChange?: (courseSlug: string) => void;
  // Only set by the specialization/professional-certificate reviews page -- swaps the "All
  // reviews" heading for "Learner reviews across #{name}".
  specializationName?: string;
};

export type ReviewWithAuthor = CourseReviewElement & {
  fullName?: string | null;
  users?: { publicDemographics?: { fullName?: string | null } | null } | null;
};

export function ReviewsTable({
  courseId,
  courseFilterOptions,
  selectedCourseSlug,
  onCourseChange,
  specializationName,
}: ReviewsTableProps) {
  const track = useTracker();
  const tableRef = React.useRef<HTMLElement | null>(null);
  const hasLoadedReviewsRef = React.useRef(false);
  const pendingPageScrollRef = React.useRef<number | null>(null);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [ratingValues, setRatingValues] = React.useState<number[]>(ALL_STAR_VALUES);
  const [productCompleted, setProductCompleted] = React.useState<boolean | undefined>(undefined);
  const [sortByHelpfulVotes, setSortByHelpfulVotes] = React.useState(true);

  const executeAction = useFluxibleAction();
  const effectiveUserId = React.useMemo(() => {
    return user.isAuthenticatedUser() ? user.get().id : undefined;
  }, []);

  const startNum = (page - 1) * pageSize;
  const overLimit = startNum + pageSize >= MAX_START_PLUS_LIMIT;
  const start = String(overLimit ? MAX_START_PLUS_LIMIT - pageSize : startNum);

  const {
    reviews: rawReviews,
    total,
    loading,
    error,
    hasData,
  } = useCourseReviews({
    courseId,
    limit: pageSize,
    start,
    ratingValues,
    productCompleted,
    sortByHelpfulVotes,
  });

  React.useEffect(() => {
    if (hasData) {
      hasLoadedReviewsRef.current = true;
    }
  }, [hasData]);

  React.useEffect(() => {
    if (pendingPageScrollRef.current === page && !loading) {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingPageScrollRef.current = null;
    }
  }, [loading, page]);

  const shouldShowInitialLoadingState = loading && !hasData && !hasLoadedReviewsRef.current;
  const shouldShowRefetchLoadingState = loading && !hasData && hasLoadedReviewsRef.current;

  const reviews: ReviewWithAuthor[] = rawReviews.filter(
    (review): review is ReviewWithAuthor => getReviewAuthorName(review) !== ''
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const selectedCourseName = courseFilterOptions?.find((course) => course.slug === selectedCourseSlug)?.name;
  const cappedPage = overLimit ? totalPages : page;
  // courseId arrives as COURSE~<id> for the reviews query; events take the bare course id
  const eventProduct = React.useMemo(() => ({ id: extractEventId(courseId), type: 'course' as const }), [courseId]);

  const handleLearnerChange = React.useCallback(
    (value: boolean) => {
      track('change_select', {
        select: {
          name: 'reviews_table_filter_learners',
          value: value ? ['completers'] : ['all_learners'],
        },
        product: eventProduct,
      });
      setProductCompleted(value);
      setPage(1);
    },
    [eventProduct, track]
  );

  const handleStarChange = React.useCallback(
    (value: number[]) => {
      track('change_select', {
        select: {
          name: 'reviews_table_filter_stars',
          value: value.length === 1 ? [`star_${value[0]}`] : ['all_stars'],
        },
        product: eventProduct,
      });
      setRatingValues(value);
      setPage(1);
    },
    [eventProduct, track]
  );

  const handleSortChange = React.useCallback(
    (helpful: boolean) => {
      track('change_select', {
        select: {
          name: 'reviews_table_sort',
          value: helpful ? ['most_helpful'] : ['most_recent'],
        },
        product: eventProduct,
      });
      setSortByHelpfulVotes(helpful);
      setPage(1);
    },
    [eventProduct, track]
  );

  const handlePageChange = React.useCallback((_e: React.ChangeEvent<unknown>, newPage: number) => {
    pendingPageScrollRef.current = newPage;
    setPage(newPage);
  }, []);

  const handlePageSizeChange = React.useCallback((_e: React.ChangeEvent<unknown>, newSize: number) => {
    pendingPageScrollRef.current = 1;
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleMarkHelpful = React.useCallback(
    (reviewId: string, voteType: string) => {
      if (!effectiveUserId || !executeAction) return;
      executeAction(submitResponse, {
        reviewData: { userId: effectiveUserId, reviewId },
        voteType,
        onSuccess: () => {},
      });
    },
    [executeAction, effectiveUserId]
  );

  const pageSizeInputOptions = React.useMemo(
    () =>
      PAGE_SIZE_VALUES.map((value) => ({
        label: _t('#{count} results per page', { count: value }),
        value,
      })),
    []
  );

  const pageSizeInputProps = { label: _t('Show') };

  if (shouldShowInitialLoadingState) {
    return (
      <div css={[styles.root, styles.loading]} data-testid="reviews-table-loading">
        <LoadingSection />
      </div>
    );
  }

  if (error) {
    return (
      <div css={styles.root} data-testid="reviews-table-error">
        <InlineNotification severity="error" title={_t('Unable to load reviews')} children={error.message} />
      </div>
    );
  }

  const displayTotal = total.toLocaleString();
  const showingLabel = _t('Showing: #{count} of #{total}', {
    count: reviews.length,
    total: displayTotal,
  });

  return (
    <section ref={tableRef} css={styles.root} data-testid="reviews-table" aria-busy={loading}>
      <header css={styles.header} data-testid="reviews-table-header">
        <Typography2 variant="subtitleLarge" component="h2">
          {specializationName ? _t('Learner reviews across #{name}', { name: specializationName }) : _t('All reviews')}
        </Typography2>
        {!shouldShowRefetchLoadingState && (
          <React.Fragment>
            <Typography2 variant="bodySecondary" color="supportText" component="p">
              {showingLabel}
            </Typography2>
          </React.Fragment>
        )}
      </header>

      <ReviewsTableToolbar
        productCompleted={productCompleted}
        ratingValues={ratingValues}
        sortByHelpfulVotes={sortByHelpfulVotes}
        onLearnerChange={handleLearnerChange}
        onStarChange={handleStarChange}
        onSortChange={handleSortChange}
        courseFilterOptions={courseFilterOptions}
        selectedCourseSlug={selectedCourseSlug}
        onCourseChange={onCourseChange}
      />

      {loading ? (
        <div css={styles.loading} data-testid="reviews-table-loading">
          <LoadingSection />
        </div>
      ) : reviews.length === 0 ? (
        <React.Fragment>
          <Typography2 variant="bodyPrimary" color="supportText" data-testid="reviews-table-empty" component="p">
            {_t('No reviews found')}
          </Typography2>
        </React.Fragment>
      ) : (
        <div css={styles.cardList} data-testid="reviews-table-list" data-e2e="reviews-list">
          {reviews.map((review) => (
            <ReviewTableCard
              key={review?.id}
              review={review}
              userId={effectiveUserId}
              onMarkHelpful={handleMarkHelpful}
              courseName={selectedCourseName}
            />
          ))}
        </div>
      )}

      {!shouldShowRefetchLoadingState && total > 0 && (
        <div css={styles.pagination} data-testid="reviews-table-pagination">
          <Pagination
            total={total}
            page={cappedPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeInputOptions={pageSizeInputOptions}
            pageSizeInputProps={pageSizeInputProps}
            showXsNav
            alignment="start"
          />
        </div>
      )}
    </section>
  );
}

export default ReviewsTable;
